import { gql } from 'graphql-request';

export const CreatorDetailsFragment = gql`
  fragment CreatorDetailsFragment on Event {
    createdBy {
      firstName
      lastName
    }
  }
`;

export const CalendarFragment = gql`
  fragment CalendarFragment on Event {
    calendarNotifications
    sendCalendarInvites
    calendarProvider
    transparency
    calendarDetails {
      creatorFirstName
      creatorLastName
      lastUpdatedAt
    }
  }
`;

export const FeedbackQuestionsFragment = gql`
  fragment FeedbackQuestionsFragment on Question {
    id
    label
    type
    options
    hasOtherOption
    sort
    shownBefore
    isRequired
    isDeleted
    properties {
      scaleMinValue
      scaleMaxValue
      scaleMinLabel
      scaleMaxLabel
    }
  }
`;

export const FeedbackSettingsFragment = gql`
  fragment FeedbackSettingsFragment on Event {
    feedbackEnable
    isFeedbackAnonymous
    feedbackSchedule {
      quantity
      unit
      beforeEventEnds
      sendAt
    }
    feedbackNotificationChannel {
      email
      slack
    }
  }
`;

export const SlackChannelSettingsFragment = gql`
  fragment SlackChannelSettingsFragment on Event {
    slackChannelAutoarchive
    slackChannelDate
    slackChannelId
    slackChannelName
    slackTeamId
    createSlackChannel
  }
`;

// Shared fragment for individual EmailReminder fields
export const EmailReminderFieldsFragment = gql`
  fragment EmailReminderFieldsFragment on EmailReminder {
    id
    subject
    message
    datetime
    notifyStatus
    selectedGuestStatuses
    schedule {
      quantity
      unit
      beforeEventBegins
    }
  }
`;

// Fragment for querying emailReminders array on Event
export const CommunicationEmailReminderFragment = gql`
  ${EmailReminderFieldsFragment}
  fragment CommunicationEmailReminderFragment on Event {
    emailReminders {
      ...EmailReminderFieldsFragment
    }
  }
`;

// Shared fragment for individual SlackReminder fields
export const SlackReminderFieldsFragment = gql`
  fragment SlackReminderFieldsFragment on SlackReminder {
    id
    message
    title
    datetime
    notifyStatus
    selectedGuestStatuses
    schedule {
      quantity
      unit
      beforeEventBegins
    }
  }
`;

// Fragment for querying slackReminders array on Event
export const CommunicationSlackReminderFragment = gql`
  ${SlackReminderFieldsFragment}
  fragment CommunicationSlackReminderFragment on Event {
    slackReminders {
      ...SlackReminderFieldsFragment
    }
  }
`;

export interface SlackChannelSettings {
  slackChannelAutoarchive?: boolean;
  slackChannelDate?: string;
  slackChannelId?: string;
  slackChannelName?: string;
  slackTeamId?: string;
  createSlackChannel?: boolean;
}

export const SelfCheckinSettingsFragment = gql`
  fragment SelfCheckinSettingsFragment on Event {
    allowUserCheckin
    sendUserCheckinEmail
    sendUserCheckinSlack
    isSelfCheckinEditable
    selfCheckinSchedule {
      unit
      quantity
      beforeEventBegins
    }
  }
`;

export const EventOrganizerFragment = gql`
  fragment EventOrganizerFragment on Event {
    organizer {
      name
      email
      imageUrl
    }
  }
`;

export const EventPublishScheduleFragment = gql`
  fragment EventPublishScheduleFragment on EventPublishSchedule {
    id
    eventId
    scheduledAt
    timezoneCode
    status
    errorMessage
    createdById
    createdAt
    updatedAt
  }
`;

export const PlusOneConfigFragment = gql`
  fragment PlusOneConfigFragment on PlusOneConfig {
    allowPlusOnes
    maxPlusOnes
    collectQuestionnaireResponses
    maxRegisteredPlusOnes
  }
`;

export const BaseEventFragment = gql`
  fragment BaseEventFragment on Event {
    id
    companyId
    title
    description
    photoUrl
    eventType
    datetimeScheduled
    datetimeScheduledChanged
    datetimeEnd
    timezoneCode
    allDay
    isVirtual
    isInPerson
    isPrivate
    hasStarted
    hasEnded
    isPastRegistrationCutoff
    registrationCutoffAt
    questionnaireCutoffAt
    limitRegistration
    isThirtyDaysAfterEventEnded
    maxAttendees
    targetAttendance
    estimatedAttendees
    guestListPrivacy
    isPublished
    locationAddress
    sendCalendarInvites
    calendarNotifications
    limitRegistration
    budget
    scheduledDeletionAt
    canRestoreFromCancellation
    totalGuests
    isGuestCheckinOpen
    totalCheckedInGuests
    totalDeactivatedGuests
    isUsingEstimatedAttendees
    estimatedAttendees
    guestHasNoneStatus
    distrolistInviteeIds
    communityInviteeIds
    t2DomainArray
    ...FeedbackSettingsFragment
    ...SelfCheckinSettingsFragment
    ...CalendarFragment
    ...CreatorDetailsFragment
    ...EventOrganizerFragment
    ...CommunicationSlackReminderFragment
    ...CommunicationEmailReminderFragment
    createdBy {
      id
      firstName
      lastName
    }
    createdAt
    ...SlackChannelSettingsFragment
    companyLocation {
      id
      name
    }
    hostOrganization {
      id
      name
    }
    virtualEventInfo {
      virtualLocation
    }
    postQuestions {
      ...FeedbackQuestionsFragment
    }
    preeventEnable
    preEventQuestions {
      ...FeedbackQuestionsFragment
    }
    zoomMeeting {
      id
    }
    googleMeeting {
      id
    }
    categories {
      id
      name
    }
    guestListTotals {
      confirmed
      incomplete
      waitlisted
    }
    plusOneConfig {
      ...PlusOneConfigFragment
    }
    publishSchedule {
      ...EventPublishScheduleFragment
    }
  }
  ${FeedbackSettingsFragment}
  ${FeedbackQuestionsFragment}
  ${SelfCheckinSettingsFragment}
  ${CalendarFragment}
  ${CreatorDetailsFragment}
  ${EventOrganizerFragment}
  ${SlackChannelSettingsFragment}
  ${CommunicationSlackReminderFragment}
  ${CommunicationEmailReminderFragment}
  ${EventPublishScheduleFragment}
  ${PlusOneConfigFragment}
`;

export type EventBasicDetailsChanges = {
  eventId: number;
  changedFields: string[];
  canResetRsvps: boolean;
  shouldRequestSendUpdateNotificationOptions: boolean;
};

export const EventChangesFragment = gql`
  fragment EventChangesFragment on EventBasicDetailsChanges {
    eventId
    changedFields
    canResetRsvps
    shouldRequestSendUpdateNotificationOptions
  }
`;
